import React from 'react';
import { Link } from 'react-router-dom';

const Employers = () => {
  const outcomes = [
    { value: '5–12%', label: 'cost savings', desc: 'Find top cost drivers, renegotiate vendors, and forecast budgets with confidence.' },
    { value: '10×', label: 'faster analysis', desc: 'AI workers ingest claims/HRIS data and surface insights in seconds, not weeks.' },
    { value: 'Higher', label: 'participation', desc: 'Targeted outreach and personalized nudges improve benefits utilization and satisfaction.' }
  ];

  const useCases = [
    { icon: '📊', title: 'Claims & Cost Drivers', desc: 'Pinpoint high-cost cohorts, trend lines by condition, and PMPM outliers to take action quickly.' },
    { icon: '🏢', title: 'Vendor Optimization', desc: 'Benchmark vendors, compare outcomes and pricing, and generate board-ready evaluations.' },
    { icon: '⚖️', title: 'Compliance & Policy', desc: 'Automate ACA/FMLA monitoring and receive alerts with recommended policy updates.' },
    { icon: '📈', title: 'Engagement & Equity', desc: 'Track utilization by location, role, and demographic bands; spot gaps and recommend nudges.' },
    { icon: '💰', title: 'Budget Forecasting', desc: 'Scenario plan by utilization/inflation assumptions and lock budgets with evidence.' },
    { icon: '📑', title: 'Executive Reporting', desc: 'One-click decks: KPI snapshots, variances, recommendations, and vendor scorecards.' }
  ];

  const steps = [
    { num: '1', title: 'Connect your data', desc: 'Securely sync claims, HRIS, and vendor exports. Our pipelines normalize messy data automatically.' },
    { num: '2', title: 'AI workers analyze 24/7', desc: 'Continuous monitoring flags anomalies, benchmarks, and compliance changes with context.' },
    { num: '3', title: 'Decide and act', desc: 'Auto-generated playbooks, vendor comparisons, and outreach assets help you move fast.' },
    { num: '4', title: 'Report to stakeholders', desc: 'Share executive summaries, trends, and forecasts — always up-to-date.' }
  ];

  return (
    <div className="page-content">
      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <p className="hero-subtitle">Solutions for Employers</p>
          <h1 className="hero-title">
            Reduce Costs, Improve Experience — <span className="gradient-text">Automatically</span>
          </h1>
          <p className="hero-description">
            Avenir AI helps People, Total Rewards, and HR Ops teams analyze claims, optimize vendors, and drive engagement — without extra headcount.
          </p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn btn-primary btn-large">Talk to a Specialist</Link>
            <a href="#how-it-works" className="btn btn-secondary btn-large">How it Works</a>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            Outcomes you can <span className="gradient-text">measure</span>
          </h2>
        </div>

        <div className="outcomes-grid">
          {outcomes.map((outcome, i) => (
            <div key={i} className="outcome-card">
              <div className="outcome-value">{outcome.value}</div>
              <div className="outcome-label">{outcome.label}</div>
              <p className="outcome-desc">{outcome.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            What employers use <span className="gradient-text">Avenir AI</span> for
          </h2>
        </div>

        <div className="use-cases-grid">
          {useCases.map((useCase, i) => (
            <div key={i} className="use-case-card">
              <div className="use-case-icon">{useCase.icon}</div>
              <h3 className="use-case-title">{useCase.title}</h3>
              <p className="use-case-desc">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="section" id="how-it-works">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            How it <span className="gradient-text">works</span>
          </h2>
        </div>

        <div className="steps-grid">
          {steps.map((step, i) => (
            <div key={i} className="step-card">
              <div className="step-num">{step.num}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-desc">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ecosystem */}
      <section className="section">
        <div className="section-center">
          <h2 className="section-heading">
            Works with your <span className="gradient-text">ecosystem</span>
          </h2>
          <p className="section-description">
            Plug into leading HRIS, TPA, and point solutions — or upload flat files. Avenir adapts to your stack.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="cta-wrapper">
          <div className="cta-glow"></div>
          <div className="cta-box">
            <h2 className="cta-title">See Avenir AI for Employers</h2>
            <p className="cta-desc">We'll show you how similar teams cut spend and boosted engagement — in weeks.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-large">Book a Demo</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Employers;